import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { reportsHttp } from '@network';
import { InitialCard } from '@components/InitialCard';

import cls from './RevenueGoalsTracker.module.css';

const dateFormat = 'MMM – yyyy';

interface Props {
    range: string;
    id?: string;
    title: string;
    setLoader: (num: number, value: boolean) => void;
}

export const RevenueGoalsTracker = ({ title, setLoader, range }: Props) => {
  const [numbers, setNumbers] = useState<any>([]);
  const chartRef = useRef<HTMLDivElement | null>(null);

  const id = 'revenue-goals-tracker';

  useEffect(() => {
    const loadNumbers = async () => {
      setLoader(7, true);
      try {
        const nextNumbers = await reportsHttp.revenueGoalsTracker({ range });
        setNumbers(nextNumbers);
      } catch (e) {
        console.error(e);
      } finally {
        setLoader(7, false);
      }
    };
    void loadNumbers();
  }, [range]);

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    const root = am5.Root.new(chartRef.current);

    root.setThemes([
      am5themes_Animated.new(root),
    ]);


    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
      }),
    );

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: 'month', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, {}),
        dateFormats: {
          month: dateFormat,
        },
        periodChangeDateFormats: {
          month: dateFormat,
        },
        tooltipDateFormat: dateFormat,
      }),
    );

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      }),
    );

    const revenueGoal = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Revenue Goal',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'revenueGoal',
        valueXField: 'date',
        stroke: am5.color('#FD8A32'),
        tooltip: am5.Tooltip.new(root, {
          labelText: 'Revenue Goal: {valueY}',
        }),
      }),
    );
    revenueGoal.strokes.template.setAll({
      stroke: am5.color('#F38336'),
      strokeWidth: 2,
    });
    revenueGoal.fills.template.setAll({
      visible: true,
      fill: am5.color('#FD8A32'),
      fillOpacity: 0.65,
    });
    revenueGoal.data.setAll(numbers);

    const realRevenue = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Real Revenue',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'realRevenue',
        valueXField: 'date',
        stroke: am5.color('#042E6B'),
      }),
    );
    realRevenue.bullets.push(() => {
      return am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 5,
          fill: realRevenue.get('stroke'),
        }),
      });
    });
    realRevenue.strokes.template.setAll({
      strokeWidth: 2,
      stroke: am5.color('#042E6B'),
    });
    realRevenue.data.setAll(numbers);

    chart.set('cursor', am5xy.XYCursor.new(root, {
      xAxis: xAxis,
      snapToSeries: [realRevenue, realRevenue],
    }));

    revenueGoal.set('tooltip', am5.Tooltip.new(root, {
      labelText: 'Revenue Goal: {valueY}',
    }));

    realRevenue.set('tooltip', am5.Tooltip.new(root, {
      labelText: 'Real Revenue: {valueY}',
    }));

    chart.set('layout', root.verticalLayout);

    chart.children.push(am5.Legend.new(root, {
      centerX: am5.percent(50),
      x: am5.percent(50),
      marginTop: 40,
    }));

    realRevenue.set('name', 'Real Revenue');
    realRevenue.set('fill', am5.color('#042E6B'));

    revenueGoal.set('name', 'Revenue Goal');
    revenueGoal.set('fill', am5.color('#F38336'));

    return () => {
      root.dispose();
    };
  }, [id, numbers]);

  return (
    <InitialCard left={title} extraPadding wrapperClass={cls.wrap}>
      <div
        id={id}
        ref={chartRef}
        className={cls.chart}
      />
      <div className={cls.legend}>
        <div className={cls.legendItem}>
          <span className={cls.legendRealIcon} />
          <span className={cls.legendLabel}>Real Revenue</span>
          <div className={cls.legendInfo}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12Z" fill="#042E6B"/>
              <path d="M10.6636 13.2419L10.0184 6H13.9816L13.3364 13.2419H10.6636ZM12.0092 18C11.4316 18 10.9524 17.828 10.5714 17.4839C10.1905 17.1398 10 16.7204 10 16.2258C10 15.7204 10.1905 15.2957 10.5714 14.9516C10.9524 14.6075 11.4316 14.4355 12.0092 14.4355C12.5868 14.4355 13.0599 14.6075 13.4286 14.9516C13.8095 15.2957 14 15.7204 14 16.2258C14 16.7204 13.8095 17.1398 13.4286 17.4839C13.0599 17.828 12.5868 18 12.0092 18Z" fill="white"/>
            </svg>
            <div className={cls.tooltip}>
              <div>This is the Sum of Two values:</div>
              <ul>
                <ol>{'1. The sum of all  Membership (Verqor M. Only) of the credits signed in the selected time lapse'}</ol>
                <ol>{'2. For the credits signed in the selected time lapse that the NBFI is Verqor, we will also sum up for all the following formula:'}</ol>
              </ul>
              <div className={cls.tooltip_formula}>
                ( Interest Rate<sub>[from credit terms]</sub> / 365 ) (Days of Credit) ( Credit )
              </div>
            </div>
          </div>
        </div>
        <div className={cls.legendItem}>
          <span className={cls.legendGoalIcon} />
          <span className={cls.legendLabel}>Revenue Goal</span>
        </div>
      </div>
    </InitialCard>
  );
};
